import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, X, CheckCheck, Swords, Trophy, Clock, AlertTriangle, Eye, MapPin, Zap, Users, ArrowLeft } from 'lucide-react';

const NOTIF_ICONS = {
  duel_received: Swords,
  duel_accepted: Swords,
  duel_refused: AlertTriangle,
  duel_ignored: AlertTriangle,
  duel_taken_by_other: AlertTriangle,
  duel_auto_won: Trophy,
  duel_completed: Trophy,
  duel_expired: Clock,
  score_submitted: Eye,
  duel_match_started: Swords,
  duel_defender_joined: Swords,
  score_disputed: AlertTriangle,
  score_resubmitted: Eye,
  match_abandoned: Trophy,
  slot_selection_expired: Clock,
  open_duel_available: MapPin,
  admin_announcement: Zap,
  new_challenge: Trophy,
  affiliation_validated: Users,
};

function ensureUTC(dateStr) {
  if (!dateStr) return dateStr;
  if (typeof dateStr === 'string' && !dateStr.endsWith('Z') && !dateStr.includes('+')) return dateStr + 'Z';
  return dateStr;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(ensureUTC(dateStr)).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "À l'instant";
  if (mins < 60) return `${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}j`;
}

export default function Notifications({ onClose, onNavigate }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [markingAll, setMarkingAll] = useState(false);

  const isStandalone = !onClose;

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['myNotifications'],
    queryFn: async () => {
      if (!user) return { notifications: [], unread_count: 0 };
      const { data: notifications } = await supabase.from('in_app_notifications').select('*').eq('user_id', user.id).order('created_date', { ascending: false }).limit(50);
      const unread_count = (notifications || []).filter(n => !n.is_read).length;
      return { notifications: notifications || [], unread_count };
    },
    refetchOnWindowFocus: true,
    staleTime: 0
  });

  const notifications = data?.notifications || [];
  const unreadCount = data?.unread_count || 0;

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await supabase.from('in_app_notifications').update({ is_read: true }).eq('user_id', user.id).eq('is_read', false);
      refetch();
      queryClient.invalidateQueries({ queryKey: ['notifUnreadCount'] });
    } catch (e) {
      console.error('Failed to mark all read:', e);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleNotifClick = async (notif) => {
    // Mark as read
    if (!notif.is_read) {
      try {
        await supabase.from('in_app_notifications').update({ is_read: true }).eq('id', notif.id);
        refetch();
        queryClient.invalidateQueries({ queryKey: ['notifUnreadCount'] });
      } catch (e) {
        console.error('Failed to mark read:', e);
      }
    }

    // Navigate if data has playground_id
    if (notif.payload?.playground_id) {
      if (onNavigate) {
        onNavigate(notif.payload.playground_id);
      } else {
        navigate(`/court/${notif.payload.playground_id}`);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0B10] overflow-auto">
      <div className="p-4 md:p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-3">
            {isStandalone && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-white hover:text-[#6E2BFF] p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <div>
              <h2 className="text-lg font-black uppercase tracking-wide text-white">
                <Bell className="inline w-5 h-5 mr-2 mb-1" />
                Notifications
              </h2>
              {unreadCount > 0 && (
                <p className="text-[#B3B6C2] text-xs mt-2">
                  {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllRead}
                disabled={markingAll}
                className="text-[#6E2BFF] hover:text-[#8B5CFF] text-xs"
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                {markingAll ? '...' : 'Tout lire'}
              </Button>
            )}
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white hover:text-[#6E2BFF]"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-[#B3B6C2]">Chargement...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-12 h-12 text-[#6E2BFF] mx-auto mb-4 opacity-50" />
            <p className="text-[#B3B6C2]">Aucune notification</p>
            <p className="text-[#B3B6C2] text-sm mt-2">
              Les événements de vos duels apparaîtront ici
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notif) => {
              const IconComponent = NOTIF_ICONS[notif.type] || Bell;
              return (
                <Card
                  key={notif.id}
                  onClick={() => handleNotifClick(notif)}
                  className={`p-4 cursor-pointer transition-all border ${
                    notif.is_read
                      ? 'bg-[#13151D] border-[#1B1E28] opacity-70'
                      : 'bg-[#13151D] border-[#6E2BFF]/40 hover:border-[#6E2BFF]'
                  }`}
                >
                  <div className="flex gap-3">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      notif.is_read ? 'bg-[#1B1E28]' : 'bg-[#6E2BFF]/20'
                    }`}>
                      <IconComponent className={`w-4 h-4 ${notif.is_read ? 'text-[#9CA0AB]' : 'text-[#6E2BFF]'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-bold truncate ${notif.is_read ? 'text-[#9CA0AB]' : 'text-white'}`}>
                          {notif.title}
                        </p>
                        <span className="text-xs text-[#9CA0AB] flex-shrink-0">
                          {timeAgo(notif.created_date)}
                        </span>
                      </div>
                      <p className={`text-xs mt-1 ${notif.is_read ? 'text-[#9CA0AB]/70' : 'text-[#B3B6C2]'}`}>
                        {notif.message}
                      </p>
                      {notif.payload?.playground_id && (
                        <p className="text-xs text-[#6E2BFF] mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          Voir le terrain
                        </p>
                      )}
                    </div>
                    {!notif.is_read && (
                      <div className="w-2 h-2 bg-[#6E2BFF] rounded-full flex-shrink-0 mt-2" />
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
